import { create } from "zustand";
import type { CampaignMoveResult, CampaignRun, CampaignStage, BattleLogEvent } from "../types";
import { campaignApi } from "../api/campaign";
import { useGameStore } from "./useGameStore";

function stageStubFromRun(run: CampaignRun): CampaignStage {
  return {
    id: run.stage_id,
    name: run.stage_name,
    order_index: 0,
    tier: 0,
    stage_number: 0,
    min_hero_level: 1,
    is_boss: false,
    reward_gold: 0,
    reward_xp: 0,
    reward_diamonds: 0,
    unlocked: true,
    completed: false,
    times_completed: 0,
  };
}

function isRunActive(run: CampaignRun | null | undefined): boolean {
  return run?.status === "ACTIVE";
}

interface CampaignRunState {
  activeStage: CampaignStage | null;
  run: CampaignRun | null;
  isRunLocked: boolean;
  lastMoveResult: CampaignMoveResult | null;
  nodeBattleResult: CampaignMoveResult | null;
  debugVisibleLog: BattleLogEvent[];
  debugReplayDone: boolean;
  isLoading: boolean;
  error: string | null;

  syncActiveRun: () => Promise<boolean>;
  enterStage: (stage: CampaignStage) => Promise<boolean>;
  loadRun: (stageId: number) => Promise<void>;
  moveToNode: (nodeId: string, autoBattleMode?: string) => Promise<void>;
  finishNodeBattle: () => Promise<void>;
  finishNodeBattleAfterBalance: (updatedRun: CampaignRun) => Promise<void>;
  abandonRun: () => Promise<void>;
  setDebugReplay: (log: BattleLogEvent[], done: boolean) => void;
  reset: () => void;
}

export const useCampaignRunStore = create<CampaignRunState>((set, get) => ({
  activeStage: null,
  run: null,
  isRunLocked: false,
  lastMoveResult: null,
  nodeBattleResult: null,
  debugVisibleLog: [],
  debugReplayDone: false,
  isLoading: false,
  error: null,

  syncActiveRun: async () => {
    try {
      const run = await campaignApi.fetchActiveRun();
      if (!isRunActive(run)) {
        set({ run, isRunLocked: false });
        return false;
      }
      set({
        run,
        isRunLocked: true,
        activeStage: stageStubFromRun(run!),
      });
      return true;
    } catch {
      set({ isRunLocked: false });
      return false;
    }
  },

  enterStage: async (stage) => {
    set({ isLoading: true, error: null, activeStage: stage, lastMoveResult: null, nodeBattleResult: null });
    try {
      const run = await campaignApi.enter(stage.id);
      set({ run, isRunLocked: isRunActive(run) });
      useGameStore.getState().pushInfoMessages(run.info_messages ?? []);
      void useGameStore.getState().refreshHero();
      return true;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to enter stage", run: null, isRunLocked: false });
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  loadRun: async (stageId) => {
    set({ isLoading: true, error: null });
    try {
      const run = await campaignApi.getRun(stageId);
      set({ run, isRunLocked: isRunActive(run) });
    } catch {
      set({ run: null, isRunLocked: false });
    } finally {
      set({ isLoading: false });
    }
  },

  moveToNode: async (nodeId, autoBattleMode) => {
    const { run } = get();
    if (!run) return;
    useGameStore.getState().captureHeroSnapshot();
    set({ isLoading: true, error: null, lastMoveResult: null, nodeBattleResult: null, debugVisibleLog: [], debugReplayDone: false });
    try {
      const result = await campaignApi.move(run.id, nodeId, autoBattleMode);
      if (result.log && result.log.length > 0) {
        set({ nodeBattleResult: result, isLoading: false });
        void useGameStore.getState().refreshHero();
        return;
      }
      useGameStore.getState().pushInfoMessages(result.info_messages ?? []);
      set({ lastMoveResult: result });
      if (result.rewards || result.energy_lost) {
        await useGameStore.getState().refreshHeroAndCheckLevelUp();
        void useGameStore.getState().refreshBalance();
      }
      if (result.dropped_items?.length) {
        useGameStore.getState().enqueueLoot(result.dropped_items);
      }
      const updated = await campaignApi.getRun(run.stage_id).catch(() => null);
      if (updated) {
        set({ run: updated, isRunLocked: isRunActive(updated) });
      } else if (result.stage_completed) {
        set({ run: { ...run, status: "COMPLETED" }, isRunLocked: false });
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to move" });
    } finally {
      set({ isLoading: false });
    }
  },

  finishNodeBattle: async () => {
    const { nodeBattleResult, run } = get();
    const loot = nodeBattleResult?.dropped_items ?? [];
    set({ nodeBattleResult: null, lastMoveResult: nodeBattleResult });
    if (!nodeBattleResult) return;
    useGameStore.getState().pushInfoMessages(nodeBattleResult.info_messages ?? []);
    if (nodeBattleResult.rewards) {
      await useGameStore.getState().refreshHeroAndCheckLevelUp();
      void useGameStore.getState().refreshBalance();
    }
    if (loot.length) useGameStore.getState().enqueueLoot(loot);
    if (nodeBattleResult.battle_result === "LOSS") {
      const updated = await campaignApi.getRun(run?.stage_id).catch(() => null);
      if (updated) {
        set({ run: updated, isRunLocked: isRunActive(updated), lastMoveResult: nodeBattleResult });
      } else {
        set({ lastMoveResult: nodeBattleResult });
      }
      return;
    }
    const updated = await campaignApi.getRun(run?.stage_id).catch(() => null);
    if (updated) {
      set({ run: updated, isRunLocked: isRunActive(updated) });
    } else if (nodeBattleResult.stage_completed) {
      set({ run: run ? { ...run, status: "COMPLETED" } : null, isRunLocked: false });
    }
  },

  finishNodeBattleAfterBalance: async (updatedRun: CampaignRun) => {
    const { nodeBattleResult } = get();
    const loot = nodeBattleResult?.dropped_items ?? [];
    set({
      nodeBattleResult: null,
      lastMoveResult: nodeBattleResult,
      run: updatedRun,
      isRunLocked: isRunActive(updatedRun),
    });
    if (!nodeBattleResult) return;
    useGameStore.getState().pushInfoMessages(nodeBattleResult.info_messages ?? []);
    if (nodeBattleResult.rewards) {
      await useGameStore.getState().refreshHeroAndCheckLevelUp();
      void useGameStore.getState().refreshBalance();
    }
    if (loot.length) useGameStore.getState().enqueueLoot(loot);
  },

  abandonRun: async () => {
    const { run, activeStage } = get();
    const stageId = run?.stage_id ?? activeStage?.id;
    try {
      await campaignApi.abandon(stageId);
    } catch {
      // Best-effort — local reset still lets the player leave the screen.
    }
    get().reset();
  },

  setDebugReplay: (log, done) => set({ debugVisibleLog: log, debugReplayDone: done }),

  reset: () =>
    set({
      activeStage: null,
      run: null,
      isRunLocked: false,
      lastMoveResult: null,
      nodeBattleResult: null,
      debugVisibleLog: [],
      debugReplayDone: false,
      error: null,
    }),
}));
